import React, { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseconfig';
import {
  checkGovOpsEntitlement,
  fetchGovOpsHealth,
  hasGovOpsAuthToken,
} from '../api/govopsPermitService';
import { resolveGovOpsApiBase } from '../api/govopsClient';
import { getGovOpsOrgId } from '../api/govopsConfig';

const isLocal =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

/**
 * RobinhoodSpace — API health + GovOps entitlement for the signed-in Firebase user.
 */
const RobinhoodSpaceStatusBar = () => {
  const [apiUp, setApiUp] = useState(null);
  const [accessState, setAccessState] = useState('unknown');
  const [accessDetail, setAccessDetail] = useState('');
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    if (!auth) return undefined;

    const refresh = async () => {
      const hasToken = await hasGovOpsAuthToken();
      setSignedIn(hasToken);

      try {
        await fetchGovOpsHealth();
        setApiUp(true);
      } catch (e) {
        setApiUp(false);
        setAccessState('api_down');
        setAccessDetail(e.message || 'API offline');
        return;
      }

      if (!hasToken) {
        setAccessState('unsigned');
        setAccessDetail('');
        return;
      }

      const probe = await checkGovOpsEntitlement();
      if (probe.state === 'ok') {
        setAccessState('ok');
        setAccessDetail('');
      } else if (probe.state === 'forbidden') {
        setAccessState('forbidden');
        setAccessDetail('GovOps not granted for this account');
      } else {
        setAccessState('error');
        setAccessDetail(probe.message || 'Check failed');
      }
    };

    refresh();
    const unsub = onAuthStateChanged(auth, () => {
      refresh();
    });
    const id = setInterval(refresh, 120000);
    return () => {
      unsub();
      clearInterval(id);
    };
  }, []);

  const orgId = getGovOpsOrgId();

  let accessLabel = 'checking…';
  if (accessState === 'ok') accessLabel = 'Ready';
  else if (accessState === 'unsigned') accessLabel = 'Sign in required';
  else if (accessState === 'forbidden') accessLabel = 'Access not granted';
  else if (accessState === 'api_down') accessLabel = accessDetail || 'Offline';
  else if (accessState === 'error') accessLabel = accessDetail || 'Unavailable';

  const accessClass =
    accessState === 'ok' ? 'rhs-ok' : accessState === 'unsigned' ? 'rhs-warn' : 'rhs-err';

  return (
    <div className="rhs-status-bar" role="status" aria-live="polite">
      <span className="rhs-status-brand">RobinhoodSpace</span>
      <span className="rhs-status-sep">·</span>
      <span>
        API:{' '}
        {apiUp === null ? (
          'checking…'
        ) : apiUp ? (
          <strong className="rhs-ok">Up</strong>
        ) : (
          <strong className="rhs-err">Down</strong>
        )}
      </span>
      <span className="rhs-status-sep">·</span>
      <span>
        Permits: <strong className={accessClass}>{accessLabel}</strong>
      </span>
      {signedIn && orgId && <span className="rhs-muted"> · org {orgId}</span>}
      {isLocal && (
        <span className="rhs-dev-hint"> · dev API {resolveGovOpsApiBase()}</span>
      )}
    </div>
  );
};

export default RobinhoodSpaceStatusBar;
